import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { portalApi } from '../services/portalApi';
import { queryKeys } from './queryKeys';
import { useApiMutation } from '../hooks/useApiMutation';
import { defaultWaits, matchWait } from '../utils/actions';

const EMPTY = [];

export function useActions(window = 'd') {
  const query = useQuery({
    queryKey: queryKeys.actions.list(window),
    queryFn: () => portalApi.actions({ window }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000
  });
  return {
    rows: query.data?.docs || EMPTY,
    tz: query.data?.timezone || 'America/Chicago',
    isLoading: query.isLoading,
    error: query.error
  };
}

export function useNeedsYou(user) {
  const { rows } = useActions('d');
  const waits = defaultWaits(user?.teamRole);
  return rows.filter((x) => (x.lane === 'needs' || x.lane === 'review') && matchWait(x, waits, user));
}

export function useAction(id) {
  return useQuery({
    queryKey: queryKeys.actions.detail(id),
    queryFn: () => portalApi.action(id),
    enabled: Boolean(id)
  });
}

export function useCallActions(callId) {
  return useQuery({
    queryKey: [...queryKeys.actions.all, 'call', callId],
    queryFn: () => portalApi.actions({ callId }),
    select: (data) => data.docs || EMPTY,
    enabled: Boolean(callId)
  });
}

export function useActionStep() {
  return useApiMutation({
    mutationFn: ({ id, step, payload }) => portalApi.actionStep(id, step, payload),
    optimistic: ({ id, step, payload }) => {
      if (step !== 'batch') return [];
      const ids = new Set(payload?.ticketIds || []);
      return [
        {
          key: queryKeys.actions.detail(id),
          update: (data) => ({
            ...data,
            tickets: (data.tickets || []).map((t) =>
              ids.has(t._id) ? { ...t, pwEnteredAt: payload?.entered ? t.pwEnteredAt || new Date().toISOString() : null } : t
            )
          })
        }
      ];
    },
    invalidate: [queryKeys.actions.all],
    error: 'Could not update this action'
  });
}

export function useHandOff() {
  return useApiMutation({
    mutationFn: (payload) => portalApi.handOff(payload),
    invalidate: [queryKeys.actions.all],
    error: 'Could not hand this off'
  });
}

export function useFlagCall() {
  return useApiMutation({
    mutationFn: ({ id, ...payload }) => portalApi.flagCall(id, payload),
    invalidate: [queryKeys.actions.all],
    error: 'Could not send this'
  });
}

export function useConversationMode(contactNumber) {
  return useQuery({
    queryKey: ['conversation-mode', contactNumber],
    queryFn: () => portalApi.conversationMode(contactNumber),
    enabled: Boolean(contactNumber)
  });
}

export function useSetConversationMode(contactNumber) {
  return useApiMutation({
    mutationFn: (mode) => portalApi.setConversationMode(contactNumber, mode),
    invalidate: [['conversation-mode', contactNumber]],
    error: 'Could not change who handles this conversation'
  });
}

export function useContactActions(contactNumber) {
  const { rows } = useActions('d');
  return rows.filter((x) => x.contact?.phone === contactNumber && x.lane !== 'done');
}
